import { googleLogout } from '@react-oauth/google';
import callApi from 'client/util/apiCaller';
import { UNAUTHORIZED } from 'http-status-codes';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { createActions } from 'redux-actions';
import { getNextPath } from './AuthReducer';
import { setError } from 'client/modules/App/AppActions';
import { getI18n } from 'client/AppReducer';

const messages = defineMessages({
  email: {
    id: 'AuthError.email',
    defaultMessage: 'Email',
  },
  password: {
    id: 'AuthError.password',
    defaultMessage: 'Password',
  },
  unexpectedError: {
    id: 'ForgotPassword.UnexpectedError',
    defaultMessage: 'Sorry! Some unexpected error has occurred, please try again.',
  },
  invalidToken: {
    id: 'ForgotPassword.InvalidToken',
    defaultMessage: 'The link you used is invalid or has already expired.',
  },
  error401: {
    id: 'AuthError.Unauthorized',
    defaultMessage: 'Invalid username or password.',
  },
  error401Signup: {
    id: 'AuthError.UnauthorizedSignup',
    defaultMessage: 'This email address is already in use.',
  },
  weakPasswordSignup: {
    id: 'AuthError.WeakPasswordSignup',
    defaultMessage: 'Password is too weak. Please ensure it meets all requirements.',
  },
});

const formatMessage = id => <FormattedMessage id={id} />;

// There are three possible states for our login
// process and we need actions for each of them
export const {
  requestLogin,
  receiveLogin,
  loginError,
  clearErrorMessage,
  requestLogout,
  receiveLogout,
  saveNextPath,
  setChangingPasswordFor,
  userCreated,
  userVerifiedEmail,
  setCurrentOrganization,
} = createActions(
  {},
  'REQUEST_LOGIN',
  'RECEIVE_LOGIN',
  'LOGIN_ERROR',
  'CLEAR_ERROR_MESSAGE',
  'REQUEST_LOGOUT',
  'RECEIVE_LOGOUT',
  'SAVE_NEXT_PATH',
  'SET_CHANGING_PASSWORD_FOR',
  'USER_CREATED',
  'USER_VERIFIED_EMAIL',
  'SET_CURRENT_ORGANIZATION',
);

const sendEmailConfirmation = (token, locale, dispatch) => {
  // Token parameter kept for backward compatibility but not used (cookies handle auth)
  callApi(dispatch, `nv/users/current/send-email-confirmation?locale=${locale}`, null, 'get');
};

export const resendEmailConfirmation = token => {
  return (dispatch, getState) => {
    const { locale } = getI18n(getState());
    sendEmailConfirmation(token, locale, dispatch);
  };
};

function trackNewUser(emailVerified, email, dispatch) {
  if (emailVerified) {
    const vkey = 'c1a651811f07578aea4e831c401dd361';
    const vid = '2115900';
    const prefix =
      document.location.protocol === 'https:'
        ? 'https://ct.capterra.com'
        : 'http://ct.capterra.com';

    (() => {
      const ct = document.createElement('script');
      ct.type = 'text/javascript';
      ct.async = true;
      ct.src = `${prefix}/capterra_tracker.js?vid=${vid}&vkey=${vkey}`;
      const s = document.getElementsByTagName('script')[0];
      s.parentNode.insertBefore(ct, s);
    })();
  }
  dispatch(userCreated(email));
  // Google users may signup with pre-verified email
  if (emailVerified) {
    dispatch(userVerifiedEmail(email));
  }
}

const loginSuccess = (dispatch, isSignup, locale, user, nextPath, replace) => {
  // Check if 2FA is required
  if (user.requiresTwoFactor) {
    replace('/2fa-verify', {
      state: {
        userId: user.userId,
        nextPath,
        hasPasskeys: user.hasPasskeys,
        provider: user.provider,
      },
    });
    return;
  }

  // Check if 2FA setup is required by organization
  if (user.requiresTwoFactorSetup) {
    dispatch(receiveLogin(user.user || user));
    replace('/2fa-setup', {
      state: {
        userId: user.userId,
        nextPath,
      },
    });
    return;
  }

  dispatch(receiveLogin(user));

  if (user.email_verified) {
    let url = '/';
    if (nextPath) {
      url = nextPath;
      dispatch(saveNextPath(null));
    }
    replace(url);
  } else if (isSignup) {
    sendEmailConfirmation(null, locale, dispatch); // No token needed - user is authenticated via cookies
    replace('/verify-email', { state: { token: null } }); // No token needed
  } else {
    replace('/verify-email-resend');
  }
  if (user.wasNew) {
    trackNewUser(user.email_verified, user.email, dispatch);
  }
};

const loginFailure = (dispatch, err, isSignup) => {
  // If there was a problem, we want to
  // dispatch the error condition
  let errorMessage;

  // Handle password validation errors for signup
  if (isSignup && err.status === 422 && err.message && err.message.includes('weak')) {
    errorMessage = formatMessage(messages.weakPasswordSignup.id);
  } else if (err.response && err.response.message === 'Validation error') {
    const keys = Object.keys(err.response.errors);
    if (keys.length > 0) {
      const field = keys[0];
      errorMessage = (
        <FormattedMessage
          id="AuthError.ValidationError"
          defaultMessage="{field} is not valid"
          values={{
            field: <FormattedMessage id={`AuthError.${field}`} />,
          }}
        />
      );
    }
  }

  if (!errorMessage) {
    const sufix = isSignup ? 'Signup' : '';
    const errorMessageDesc = messages[`error${err.status}${sufix}`]
      ? messages[`error${err.status}${sufix}`]
      : messages.unexpectedError;
    errorMessage = formatMessage(errorMessageDesc.id);
  }
  dispatch(loginError(errorMessage));
};

export function signInAs(email, replace) {
  return (dispatch, getState) => {
    const state = getState();
    const { locale } = getI18n(state);
    return callApi(dispatch, 'sign-in-as', null, 'post', { email })
      .then(res => {
        loginSuccess(dispatch, false, locale, res.user, null, replace);
      })
      .catch(err => {
        loginFailure(dispatch, err, false);
      });
  };
}

// Calls the API to get a token and dispatches actions along the way
export function loginUser(creds, isSignup, replace) {
  return (dispatch, getState) => {
    const state = getState();
    const { locale } = getI18n(state);
    const nextPath = getNextPath(state);
    // We dispatch requestLogin to kickoff the call to the API
    dispatch(requestLogin(creds));
    callApi(dispatch, 'public/auth', null, 'post', { ...creds, isSignup })
      .then(res => {
        // Handle 2FA required response
        if (res.requiresTwoFactor) {
          loginSuccess(dispatch, isSignup, locale, res, nextPath, replace);
        } else if (res.requiresTwoFactorSetup) {
          // Handle 2FA setup required by organization
          loginSuccess(dispatch, isSignup, locale, res, nextPath, replace);
        } else {
          loginSuccess(dispatch, isSignup, locale, res.user, nextPath, replace);
        }
      })
      .catch(err => {
        loginFailure(dispatch, err, isSignup);
      });
  };
}

// Calls the API to get a token and dispatches actions along the way
export function confirmEmail(token, replace) {
  return (dispatch, getState) => {
    const { locale } = getI18n(getState());
    const nextPath = getNextPath(getState());
    return callApi(dispatch, `public/confirm-email/${token}`, null, 'get')
      .then(res => {
        loginSuccess(dispatch, false, locale, res.user, nextPath, replace);
        dispatch(userVerifiedEmail(res.user.email));
      })
      .catch(({ response }) => {
        const status = response ? response.status : '500';
        replace(`/sign-up?confirmEmailError=${status}`);
      });
  };
}

// Logs the user out
export function logoutUser() {
  return async dispatch => {
    // Clear Redux state immediately
    dispatch(requestLogout());
    dispatch(receiveLogout());

    // Clear Google OAuth
    if (typeof window !== 'undefined') {
      googleLogout();
    }

    // Call server to revoke session and clear httpOnly cookies
    // Server will get refresh token from httpOnly cookies
    try {
      await callApi(dispatch, 'auth/logout', null, 'post');
    } catch (err) {
      // Don't fail logout if server call fails
      console.log('Server logout failed, but client logout completed:', err);
    }
  };
}

export function sendPasswordReminder(email, locale) {
  return dispatch => {
    return callApi(dispatch, 'public/password-reminder', null, 'post', { email, locale });
  };
}

export function passwordReminderCheck(id, token) {
  return dispatch => {
    callApi(dispatch, `public/password-reminder/${id}/${token}`, null, 'get')
      .then(({ response }) => {
        dispatch(setChangingPasswordFor(response.email));
      })
      .catch(({ response }) => {
        const error =
          response && response.status && response.status === UNAUTHORIZED
            ? formatMessage('ForgotPassword.InvalidToken')
            : formatMessage('ForgotPassword.UnexpectedError');
        dispatch(setError(error));
      });
  };
}

export function passwordReminderChange(id, token, password, replace) {
  return (dispatch, getState) => {
    callApi(dispatch, `public/password-reminder/${id}/${token}`, null, 'post', { password })
      .then(res => {
        const { locale } = getI18n(getState());
        const nextPath = getNextPath(getState());
        replace('/');
        loginSuccess(dispatch, false, locale, res.user, nextPath, replace);
      })
      .catch(({ response }) => {
        const error =
          response && response.status && response.status === UNAUTHORIZED
            ? formatMessage('ForgotPassword.InvalidToken')
            : formatMessage('ForgotPassword.UnexpectedError');
        dispatch(setError(error));
      });
  };
}

export function updatePassword(currentPassword, newPassword) {
  return (dispatch, _getState) => {
    return callApi(dispatch, 'auth/password', null, 'put', { currentPassword, newPassword });
  };
}

export function updateEmailAction(newEmail) {
  return (dispatch, getState) => {
    const { locale } = getI18n(getState());
    return callApi(dispatch, 'user/email', null, 'put', { newEmail, locale });
  };
}

export function deleteMeAction() {
  return (dispatch, _getState) => {
    return callApi(dispatch, 'user/me', null, 'delete');
  };
}

export function updateUserLocaleAction(locale) {
  return (dispatch, getState) => {
    const state = getState();
    const profile = state.auth?.profile;

    // Only update database if user is authenticated
    if (!profile || !profile.id) {
      // For anonymous users, just return resolved promise
      // The locale will be handled by the AppActions.setLocale
      return Promise.resolve();
    }

    return callApi(dispatch, 'user/locale', null, 'put', { locale });
  };
}

export function chooseOrganization(orgId) {
  return (dispatch, _getState) => {
    dispatch(setCurrentOrganization(orgId));
    return callApi(dispatch, `users/current/organization`, null, 'PUT', {
      organization: orgId,
    }).then(res => {
      // Update the user profile with the new token and organization data
      dispatch(receiveLogin(res.user));
      // Cookies are now handled centrally in the receiveLogin action
      return res;
    });
  };
}

export const getStateAndExtra = () => (_, getState, extra) => ({ state: getState(), extra });

export function fetchInvitation(invitationId, token) {
  return async dispatch => {
    try {
      const result = await callApi(
        dispatch,
        `public/accept-invitation/${invitationId}/${token}`,
        null,
        'get',
      );
      return result;
    } catch (err) {
      if (err.bodyJson?.error === 'This invitation is not for your email address') {
        throw { ...err, invitation: err.bodyJson.invitation };
      }
      throw err;
    }
  };
}

export function acceptInvitation(invitationId, token, data) {
  return (dispatch, _getState) => {
    return callApi(dispatch, `public/accept-invitation/${invitationId}`, null, 'post', {
      token,
      ...data,
    })
      .then(res => {
        if (res.token) {
          dispatch(receiveLogin(res.user));
          // Cookies are now handled centrally in the receiveLogin action
        }
        return res;
      })
      .catch(err => {
        if (err.bodyJson?.error === 'This invitation is not for your email address') {
          throw err;
        }
        loginFailure(dispatch, err, true);
        throw err;
      });
  };
}

export function checkAuthPolicy(email) {
  return dispatch => {
    return callApi(
      dispatch,
      `public/auth/policy?email=${encodeURIComponent(email.trim().toLowerCase())}`,
      null,
      'get',
    );
  };
}
